import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  Message,
  updateMessageOnDb,
  upsertMessages,
} from "@database/messages/saveMessages"
import { getMessagesFromDb } from "@app/helpers/actions/getMessagesFromDb"
import { useSession } from "next-auth/react"

interface GlobalContextProps {
  messages: Message[]
  setMessages: Dispatch<SetStateAction<Message[]>>
  refreshMessageDataInContext: () => Promise<void>
  loadingMessages: boolean
  addMessages: (newMessages: Message[]) => void
  updateMessage: (updatedMessage: Partial<Message>) => void
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const MessageContext: React.FC<GlobalProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const { data: session, status } = useSession()

  const refreshMessageDataInContext = useCallback(async () => {
    if (!session?.user) {
      console.log("MessageContext: No session, skipping messages fetch")
      return
    }

    setLoadingMessages(true)

    try {
      const messageData = await getMessagesFromDb()
      if (messageData) {
        const { messages } = JSON.parse(messageData)
        await setMessages(messages)
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error)
    } finally {
      setLoadingMessages(false)
    }
  }, [session?.user])

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    if (session?.user) {
      // Defer message fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      const deferFetch = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              refreshMessageDataInContext()
            },
            { timeout: 2000 }
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            refreshMessageDataInContext()
          }, 100) // Small delay to allow initial render
        }
      }
      deferFetch()
    }
  }, [session, status, refreshMessageDataInContext])

  const addMessages = useCallback(async (newMessages: Message[]) => {
    // Optimistically add messages with a loading flag
    setMessages((prev) => [
      ...prev,
      ...newMessages.map((message) => ({ ...message, loading: true })),
    ])

    try {
      // Persist the changes to the database
      await upsertMessages(newMessages)

      // On success, remove the loading flag
      setMessages((prev) =>
        prev.map((message) =>
          newMessages.some(
            (newMessage) => newMessage.messageId === message.messageId
          )
            ? { ...message, loading: false }
            : message
        )
      )
    } catch (error) {
      // On failure, remove the optimistically added messages
      console.error("Failed to add messages:", error)

      setMessages((prev) =>
        prev.filter(
          (message) =>
            !newMessages.some(
              (newMessage) => newMessage.messageId === message.messageId
            )
        )
      )
    }
  }, [])

  const updateMessage = useCallback(
    async (updatedMessage: Partial<Message>) => {
      // Optimistically update state
      setMessages((prev) =>
        prev.map((message) =>
          message.messageId === updatedMessage.messageId
            ? { ...message, ...updatedMessage, loading: true }
            : message
        )
      )

      try {
        // Persist the changes to the database
        await updateMessageOnDb(updatedMessage)

        // Set the message's loading state to false on success
        setMessages((prev) =>
          prev.map((message) =>
            message.messageId === updatedMessage.messageId
              ? { ...message, loading: false }
              : message
          )
        )
      } catch (error) {
        // Revert the state if the update fails
        console.error("Failed to update message:", error)

        setMessages((prev) =>
          prev.filter(
            (message) => message.messageId !== updatedMessage.messageId
          )
        )
      }
    },
    []
  )

  return (
    <GlobalContext.Provider
      value={{
        messages,
        setMessages,
        refreshMessageDataInContext,
        loadingMessages,
        addMessages,
        updateMessage,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useMessageContext = () => useContext(GlobalContext)
